/**
 * Door-aware walkability: the live predicate the movement system steps through,
 * composing the map's static walkability mask with the current door state
 * (Doors & Signs - Slice 4, issue #35).
 *
 * Today's map walkability is a load-time constant ({@link GameMap.isWalkable}
 * over `MapData.blocked`). A stateful door's cell is walkable or not depending on
 * its live `open` flag, which the static mask cannot express. So instead of
 * binding the movement system to `map.walkableFn()`, it binds to
 * {@link doorAwareWalkable}: the static mask AND "no closed door occupies this
 * cell". Building it once per tick (a small `Set` of closed-door cells) keeps the
 * per-step predicate O(1) and pure - it reads only the world, no clock.
 *
 * The ADR-0005 carve already made the entrance cells statically walkable ("no
 * static wall here"); this is what lets a *closed* door re-block them without the
 * carve and the door fighting. The door entity is the authority for those cells'
 * walkability now (see ADR-0014).
 */
import type { CanStep, IsWalkable } from './mobile';
import type { World } from './world';

/** Cell key for the closed-door lookup set. */
function cellKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * The set of cells currently blocked by a **closed** door. An open door
 * contributes nothing, so its cell falls back to the static mask (walkable where
 * the carve opened it).
 */
export function closedDoorCells(world: World): Set<string> {
  const cells = new Set<string>();
  for (const entity of world.all()) {
    const door = entity.door;
    if (door && !door.open && entity.position) {
      cells.add(cellKey(entity.position.x, entity.position.y));
    }
  }
  return cells;
}

/**
 * Build the door-aware {@link IsWalkable} for the world's current state: a cell
 * is walkable iff the static map allows it AND no closed door sits on it. Snapped
 * once (the closed-door set is captured up front), so feeding it to many mobiles
 * in one tick stays cheap.
 */
export function doorAwareWalkable(world: World): IsWalkable {
  const staticWalkable = world.map.walkableFn();
  const blocked = closedDoorCells(world);
  return (x, y) => staticWalkable(x, y) && !blocked.has(cellKey(x, y));
}

/**
 * The walkability predicate for an entity standing at level `z`.
 *
 * Ground level (`z` 0 / absent) is exactly {@link doorAwareWalkable}: the static
 * mask AND live door state. An elevated level is a different surface entirely -
 * the roof plate: a cell is standable iff a roof static exists at that tile and
 * z ({@link GameMap.isRoofWalkable}). The ground mask and the doors deliberately
 * do not apply up there: the bank's interior walls must not block the open roof,
 * and its open doors must not let a rooftop walker step off the plate - the
 * plate's own edge (no roof cell beyond it) is the only boundary.
 */
export function walkableForLevel(world: World, z: number): IsWalkable {
  if (!z) return doorAwareWalkable(world);
  return (x, y) => world.map.isRoofWalkable(x, y, z);
}

/**
 * The edge-based step predicate for a level (true terrain height, PRD #536
 * Slice A): {@link walkableForLevel}'s per-cell rule on the destination,
 * composed with the map's **climb rule** ({@link GameMap.canStep} -
 * `|landZ(to) - landZ(from)| <= CLIMB`).
 *
 * `z` is a **level selector**, same convention as {@link walkableForLevel}:
 * pass 0 for the ground level (where terrain height applies) and the plate z
 * for a roof level. Roof plates are flat surfaces at a single z, so the climb
 * rule deliberately does not apply up there - the plate's own edge is the only
 * boundary. On a flat map (no `heights`) the ground predicate degenerates to
 * exactly the old per-cell one, so behavior is byte-for-byte unchanged.
 */
export function canStepForLevel(world: World, z: number): CanStep {
  const walkable = walkableForLevel(world, z);
  if (z) return (_from, to) => walkable(to.x, to.y);
  const map = world.map;
  return (from, to) => walkable(to.x, to.y) && map.canStep(from, to);
}
